'use client';

import { Suspense, useState, FormEvent } from 'react';
import { signIn } from 'next-auth/react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { Eye, EyeOff, ArrowLeft, Loader2 } from 'lucide-react';
import { getLoginErrorMessage, isRetryableGoogleError } from './errors';
import PrivacyNoticeLink from '@/components/legal/PrivacyNoticeLink';

type UserRole = 'student' | 'teacher' | 'admin';
type AuthType = 'wonde' | 'credentials' | 'both';

const DASHBOARD_ROUTES: Record<UserRole, string> = {
  student: '/student/dashboard',
  teacher: '/teacher/dashboard',
  admin: '/admin/home',
};

type LoginStep = 'email' | 'password' | 'sso-redirect';

function formatCommitTime(iso: string | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
}

function VersionFooter() {
  const sha = process.env.NEXT_PUBLIC_COMMIT_SHA ?? 'dev';
  const time = formatCommitTime(process.env.NEXT_PUBLIC_COMMIT_TIME);
  return (
    <footer className="fixed bottom-3 inset-x-0 text-center text-xs text-grayscale-300 font-baloo-2 select-none">
      v {sha}
      {time && <span> · {time}</span>}
    </footer>
  );
}

/**
 * aide e40098d4: the way to the Reset your password screen. It sits under the
 * last box, just above the purple button, in the same place on both steps,
 * and takes the email along if one has been typed.
 */
function ForgotPasswordLink({ email }: { email: string }) {
  const typed = email.trim();
  const href = typed ? `/forgot-password?email=${encodeURIComponent(typed)}` : '/forgot-password';
  return (
    <div className="-mb-4 flex justify-end">
      <Link
        href={href}
        className="inline-flex min-h-[2.75rem] items-center text-sm font-medium text-[#9056F5] hover:underline font-baloo-2"
      >
        Forgot password?
      </Link>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
      <PrivacyNoticeLink />
      <VersionFooter />
    </Suspense>
  );
}

function LoginForm() {
  const searchParams = useSearchParams();
  const callbackError = searchParams.get('error');
  const initialError = getLoginErrorMessage(callbackError);
  const initialErrorRetryable = isRetryableGoogleError(callbackError);

  // Step state
  const [step, setStep] = useState<LoginStep>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState(initialError);
  const [loading, setLoading] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [hasGoogle, setHasGoogle] = useState(false);

  const errorIsRetryableSSO = initialErrorRetryable && error === initialError;

  function handleGoogleRetry() {
    setStep('sso-redirect');
    signIn('google', { callbackUrl: '/post-login' });
  }

  // Step 1: Check email auth type
  async function handleEmailSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    const trimmed = email.trim();
    if (!trimmed) {
      setError('Please enter your email');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/check-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: trimmed }),
      });
      const json = await res.json();
      if (!json.data?.found) {
        setError("We couldn't find an account with that email.");
        setLoading(false);
        return;
      }
      const type = json.data.auth_type as AuthType;
      setHasPassword(json.data.has_password);
      setHasGoogle(Boolean(json.data.has_google));

      if (type === 'wonde' || (type === 'both' && !json.data.has_password)) {
        setStep('sso-redirect');
        window.location.href = '/api/auth/mylogin/authorize';
      } else {
        setStep('password');
        setLoading(false);
      }
    } catch {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  }

  // Step 2: Credential login
  async function handlePasswordSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError('');
    if (!password.trim()) {
      setError('Please enter your password');
      return;
    }
    setLoading(true);
    try {
      // NextAuth v5 beta's signIn({ redirect: false }) response shape is
      // unreliable with the Credentials provider — the callback always 302s
      // to an HTML page, so result.error is set even when the session cookie
      // is written correctly. Trust the session, not the signIn result.
      await signIn('credentials', {
        redirect: false,
        email: email.trim(),
        password,
      }).catch(() => null);
      const sessionRes = await fetch('/api/auth/session', { cache: 'no-store' });
      const session = await sessionRes.json();
      const role = session?.user?.role as UserRole | undefined;
      if (!role) {
        setError('Invalid email or password');
        setLoading(false);
        return;
      }
      // FLO-219: hard navigation, not router.push. The dashboard route may
      // redirect server-side (e.g. the /student/pre-assessment gate). A soft
      // navigation that lands on a redirect can leave the App Router stuck
      // re-fetching the destination's RSC payload in a loop. A full page load
      // re-runs middleware, layouts, and pages from scratch with the cookie
      // already in place.
      window.location.assign(DASHBOARD_ROUTES[role]);
    } catch {
      setError('An unexpected error occurred');
      setLoading(false);
    }
  }

  function handleBack() {
    setStep('email');
    setPassword('');
    setError('');
  }

  // SSO redirect interstitial
  if (step === 'sso-redirect') {
    return (
      <main className="min-h-screen bg-white md:bg-[#F3ECFF] flex items-center justify-center md:p-6">
        <div className="flex h-screen md:h-auto flex-col items-center justify-center bg-white px-6 overflow-y-auto md:rounded-[1.875rem] md:shadow-[0_5px_20px_0_rgba(14,4,32,0.04)] md:px-12 md:py-16 md:max-w-[480px] md:w-full">
          <div className="mb-6">
            <Image src="/educity-wordmark.svg" alt="EduCity" width={210} height={55} priority />
          </div>
          <Loader2 className="h-8 w-8 animate-spin text-brand-purple mb-4" />
          <p className="text-lg font-medium text-brand-dark font-baloo-2">
            Taking you to your school login...
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-white md:bg-[#F3ECFF] flex items-center justify-center md:p-6">
      <div className="flex h-screen md:h-auto flex-col items-center justify-center bg-white px-6 overflow-y-auto md:rounded-[1.875rem] md:shadow-[0_5px_20px_0_rgba(14,4,32,0.04)] md:px-12 md:py-16 md:max-w-[480px] md:w-full">
        <h1 className="mb-6 text-[1.625rem] font-bold text-[#9056F5] font-baloo">
          Welcome to EduCity!
        </h1>

        {/* Step 1: Email entry */}
        {step === 'email' && (
          <>
            <form onSubmit={handleEmailSubmit} className="w-full max-w-[327px] space-y-6" noValidate>
              {error && (
                <div
                  className="rounded-md bg-red-50 p-4 text-sm text-red-800 border border-red-200"
                  role="alert"
                >
                  <p>{error}</p>
                  {errorIsRetryableSSO && (
                    <button
                      type="button"
                      onClick={handleGoogleRetry}
                      className="mt-3 inline-flex items-center justify-center gap-2 min-h-[2.75rem] py-2 px-4 text-sm font-medium text-brand-dark bg-white border-2 border-gray-200 rounded-[0.625rem] cursor-pointer transition-colors duration-150 hover:bg-gray-50 font-baloo-2"
                    >
                      <Image src="/google-logo.svg" alt="" width={18} height={18} aria-hidden />
                      <span>Try again</span>
                    </button>
                  )}
                </div>
              )}

              <div>
                <div className="relative">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    aria-label="Email address"
                    placeholder="Email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-[80px] w-full rounded-xl-plus border-2 border-[#F3F3F3] px-4 text-base font-baloo-2 placeholder:font-baloo-2 placeholder:font-medium placeholder:text-[1.25rem] placeholder:text-[#C6C4CB] focus:border-[#9056F5] focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={loading}
                    required
                  />
                </div>
                <ForgotPasswordLink email={email} />
              </div>

              <button
                type="submit"
                className="h-[54px] w-full rounded-[0.625rem] bg-[#9056F5] text-[1.375rem] font-normal text-white hover:bg-[#7d45e8] font-baloo disabled:opacity-50 disabled:cursor-not-allowed min-h-[2.75rem]"
                disabled={loading}
              >
                {loading ? 'Checking...' : 'Log in'}
              </button>
            </form>

            <div className="flex items-center gap-3 my-5 mx-0">
              <span className="text-sm text-gray-400 lowercase">or</span>
            </div>

            <button
              type="button"
              aria-label="Sign in with Google"
              onClick={handleGoogleRetry}
              className="flex items-center justify-center gap-3 w-full max-w-sm min-h-[2.75rem] py-2 px-4 text-base font-medium text-brand-dark bg-gray-100 border-2 border-gray-200 rounded-[0.625rem] cursor-pointer transition-colors duration-150 hover:bg-gray-200 font-baloo-2"
            >
              <Image src="/google-logo.svg" alt="" width={20} height={20} aria-hidden />
              <span>Sign in with Google</span>
            </button>
          </>
        )}

        {/* Step 2: Password entry (credential / both users) */}
        {step === 'password' && (
          <>
            <form onSubmit={handlePasswordSubmit} className="w-full max-w-[327px] space-y-6" noValidate>
              {error && (
                <div
                  className="rounded-md bg-red-50 p-4 text-sm text-red-800 border border-red-200"
                  role="alert"
                >
                  <p>{error}</p>
                  {errorIsRetryableSSO && (
                    <button
                      type="button"
                      onClick={handleGoogleRetry}
                      className="mt-3 inline-flex items-center justify-center gap-2 min-h-[2.75rem] py-2 px-4 text-sm font-medium text-brand-dark bg-white border-2 border-gray-200 rounded-[0.625rem] cursor-pointer transition-colors duration-150 hover:bg-gray-50 font-baloo-2"
                    >
                      <Image src="/google-logo.svg" alt="" width={18} height={18} aria-hidden />
                      <span>Try again</span>
                    </button>
                  )}
                </div>
              )}

              <div className="relative">
                <input
                  type="email"
                  value={email}
                  disabled
                  className="h-[80px] w-full rounded-xl-plus border-2 border-[#F3F3F3] bg-gray-50 px-4 text-base font-baloo-2 text-grayscale-500 cursor-not-allowed"
                />
              </div>

              <div>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                    className="h-[80px] w-full rounded-xl-plus border-2 border-[#F3F3F3] px-4 pr-12 text-base font-baloo-2 placeholder:font-baloo-2 placeholder:font-medium placeholder:text-[1.25rem] placeholder:text-[#C6C4CB] focus:border-[#9056F5] focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={loading}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                <ForgotPasswordLink email={email} />
              </div>

              <button
                type="submit"
                className="h-[54px] w-full rounded-[0.625rem] bg-[#9056F5] text-[1.375rem] font-normal text-white hover:bg-[#7d45e8] font-baloo disabled:opacity-50 disabled:cursor-not-allowed min-h-[2.75rem]"
                disabled={loading}
              >
                {loading ? 'Logging in\u2026' : 'Continue'}
              </button>
            </form>

            {hasGoogle && (
              <>
                <div className="flex items-center gap-3 my-5 mx-0">
                  <span className="text-sm text-gray-400 lowercase">or</span>
                </div>
                <button
                  type="button"
                  aria-label="Sign in with Google"
                  onClick={handleGoogleRetry}
                  className="flex items-center justify-center gap-3 w-full max-w-sm min-h-[2.75rem] py-2 px-4 text-base font-medium text-brand-dark bg-gray-100 border-2 border-gray-200 rounded-[0.625rem] cursor-pointer transition-colors duration-150 hover:bg-gray-200 font-baloo-2"
                >
                  <Image src="/google-logo.svg" alt="" width={20} height={20} aria-hidden />
                  <span>Sign in with Google</span>
                </button>
              </>
            )}

            {!hasPassword && (
              <div className="mt-4 rounded-md bg-amber-50 p-4 text-sm text-amber-800 border border-amber-200 w-full max-w-sm">
                You don&apos;t have a password set. Sign in with Google above, or ask your teacher
                for help.
              </div>
            )}

            <button
              onClick={handleBack}
              className="flex items-center gap-1 min-h-[2.75rem] mt-4 text-sm font-medium text-brand-purple cursor-pointer bg-transparent border-none hover:underline font-baloo-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
          </>
        )}
      </div>
    </main>
  );
}
